import { Component, DoCheck, Input, OnInit } from '@angular/core';

import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { ScheduledEvent, WaitlistEntry } from '../../../../../../../../../../../models/scheduled-event';
import {
  batchAll,
  CallbackResult,
  CallbackResultStatus,
  dateStringFromISO8601,
  timeStringFromISO8601,
} from '../../../../../../../../../../../utils';
import { ScheduledEventService } from '../../../../../../../../../../../services/scheduled-event/scheduled-event.service';
import { Event } from '../../../../../../../../../../../models/event';
import { EventService } from '../../../../../../../../../../../services/event/event.service';
import { EventTypeService } from '../../../../../../../../../../../services/event-type/event-type.service';
import { EventType } from '../../../../../../../../../../../models/event-type';
import { AgeRestrictionBypass, eventAgeAsOfDate, waitlistEntryAgeBirthday } from '../../../../../../../../../../../models/age-restriction';


@Component({
  selector: 'app-resend-pending-waitlist-invitations-modal',
  templateUrl: './resend-pending-waitlist-invitations-modal.component.html',
  styleUrls: ['./resend-pending-waitlist-invitations-modal.component.css']
})
export class ResendPendingWaitlistInvitationsModalComponent implements OnInit, DoCheck {

  @Input() scheduledEvent: ScheduledEvent;
  @Input() pendingEntries: WaitlistEntry[] = [];
  @Input() resentCallback: (result: CallbackResult) => void;

  public readonly ageRestriction = new AgeRestrictionBypass();

  public event: Event = null;
  public eventType: EventType = null;
  public loadingEvent = false;
  public loadingEventType = false;

  public sendInviteToParentsAndGuardians = false;

  public loadingSave = false;
  public numberSaved = 0;

  public errorMessage = '';
  public serverError: number = null;

  public readonly dateStringFromISO8601 = dateStringFromISO8601;
  public readonly timeStringFromISO8601 = timeStringFromISO8601;

  constructor(
    public activeModal: NgbActiveModal,
    private scheduledEventService: ScheduledEventService,
    private eventService: EventService,
    private eventTypeService: EventTypeService,
  ) {}

  ngOnInit() {
    this.loadEventType();
    this.loadEvent();
  }

  ngDoCheck() {
    if ((this.event === null) || (this.eventType === null)) {
      this.ageRestriction.refresh('', [], null);
      return;
    }

    this.ageRestriction.refresh(
      'event-' + this.event.id.toString(),
      this.pendingEntries.map(entry => ({
        key: entry.id,
        name: entry.first_name + ' ' + entry.last_name,
        birthday: waitlistEntryAgeBirthday(entry),
      })),
      this.eventType,
      eventAgeAsOfDate(this.event)
    );
  }

  public getLoadingProgressPercentageStr(): string {
    if (this.pendingEntries.length === 0) { return '0'; }
    return Math.ceil((this.numberSaved / this.pendingEntries.length) * 100).toString();
  }

  public resendAllowed(): boolean {
    return (
      (this.pendingEntries.length > 0) &&
      !this.loadingEvent &&
      !this.loadingEventType &&
      !this.ageRestriction.blocksSend()
    );
  }

  public handleResend(): Promise<void> {
    this.errorMessage = '';
    this.loadingSave = true;

    this.numberSaved = 0;
    let successfulCount = 0;
    const failedNamesByReason = new Map<string, string[]>();

    const entryHandler = (entry: WaitlistEntry): Promise<void> => {
      if ((this.errorMessage !== '') || (this.serverError !== null)) {
        return Promise.resolve();
      }

      // No deadline or note in the body: the server then keeps the entry's
      // current ones, so the invitation goes out unchanged.
      return this.scheduledEventService
        .inviteWaitlistEntryToBook(
          entry.id,
          undefined,
          undefined,
          undefined,
          undefined,
          this.sendInviteToParentsAndGuardians,
          this.ageRestriction.codesFor(entry.id),
          true
        )
        .then(() => { successfulCount++; })
        .catch(reason => {
          if (reason.status === 403) {
            this.errorMessage = 'You do not have permission to perform this action.';
          } else if (reason.status === 400) {
            // Not counted as successful; the reason is reported in the summary
            // below. Only a string body is a refusal message to show.
            if (typeof reason.error === 'string') {
              const named = failedNamesByReason.get(reason.error) || [];
              named.push(entry.first_name + ' ' + entry.last_name);
              failedNamesByReason.set(reason.error, named);
            }
          } else {
            this.serverError = reason.status;
          }
        })
        .finally(() => { this.numberSaved++; });
    };

    return batchAll(
      4,
      this.pendingEntries,
      entryHandler
    )
      .then(() => {
        if (this.errorMessage !== '') { return; }

        const failedCount = this.pendingEntries.length - successfulCount;

        let message = (
          successfulCount.toString() +
          ' invitation' +
          ((successfulCount === 1) ? ' was' : 's were') +
          ' resent successfully.'
        );
        if (failedCount > 0) {
          message += (
            ' ' +
            failedCount.toString() +
            ' invitation' +
            ((failedCount === 1) ? '' : 's') +
            ' could not be resent'
          );
          const reasonsText = this.buildFailureReasonsText(failedNamesByReason);
          message += (reasonsText !== '') ? ('. ' + reasonsText) : '.';
        }

        this.resentCallback({
          text: message,
          status: (successfulCount > 0) ? CallbackResultStatus.success : CallbackResultStatus.error
        });
        this.activeModal.close();
      })
      .finally(() => { this.loadingSave = false; });
  }

  /**
   * Render the refusal reasons the server gave, each prefixed with the
   * participants it applies to. The server writes these for one participant,
   * so a bare list of them in a bulk summary would not say who any is about.
   */
  private buildFailureReasonsText(failedNamesByReason: Map<string, string[]>): string {
    const parts: string[] = [];

    failedNamesByReason.forEach((names, reason) => {
      parts.push(names.join(', ') + ': ' + reason);
    });

    return parts.join(' ');
  }

  private loadEventType() {
    this.loadingEventType = true;

    this.eventTypeService
      .getEventType(this.scheduledEvent.event_type_id)
      .then(eventType => { this.eventType = eventType; })
      .catch(reason => { this.serverError = reason.status; })
      .finally(() => { this.loadingEventType = false; });
  }

  private loadEvent() {
    this.loadingEvent = true;

    this.eventService
      .getEvent(this.scheduledEvent.event_id)
      .then(event => { this.event = event; })
      .catch(reason => { this.serverError = reason.status; })
      .finally(() => { this.loadingEvent = false; });
  }
}
